// src/pages/league-dashboard/salary-contract/components/PriceDistributionSummary.js
import React from 'react';
import { Link } from 'react-router-dom';
import { BarChart3 } from 'lucide-react';
import { dynastyTheme } from '../../../../services/colorService';
import PlayerLink from '../../../../components/PlayerLink';

const PriceDistributionSummary = ({ generatedPrices, league, leagueId }) => {
  
  const getDistributionChart = () => {
    if (!generatedPrices?.prices) return null;

    const ranges = [
      { label: '$50+', min: 50, max: Infinity, count: 0, color: 'bg-red-500' },
      { label: '$40-49', min: 40, max: 49, count: 0, color: 'bg-orange-500' },
      { label: '$30-39', min: 30, max: 39, count: 0, color: 'bg-yellow-500' },
      { label: '$20-29', min: 20, max: 29, count: 0, color: 'bg-green-500' },
      { label: '$10-19', min: 10, max: 19, count: 0, color: 'bg-cyan-400' },
      { label: '$5-9', min: 5, max: 9, count: 0, color: 'bg-indigo-500' },
      { label: '$2-4', min: 2, max: 4, count: 0, color: 'bg-purple-500' }
    ];

    generatedPrices.prices.forEach(player => {
      const baselinePrice = player.pricing?.baseline_price || 0;
      // Handle edge cases: price of 0 or 1 goes to lowest bucket
      if (baselinePrice < 2) {
        ranges[ranges.length - 1].count++;
      } else {
        const range = ranges.find(r => baselinePrice >= r.min && baselinePrice <= r.max);
        if (range) range.count++;
      }
    });

    // Calculate total for percentage display
    const total = generatedPrices.prices.length;
    const maxCount = Math.max(...ranges.map(r => r.count));

    return ranges.map(range => ({
      ...range,
      percentage: maxCount > 0 ? (range.count / maxCount) * 100 : 0,
      percentOfTotal: total > 0 ? ((range.count / total) * 100).toFixed(1) : 0
    }));
  };

  const getTop10Players = () => {
    if (!generatedPrices?.prices) return [];

    return [...generatedPrices.prices]
      .sort((a, b) => (b.pricing?.baseline_price || 0) - (a.pricing?.baseline_price || 0))
      .slice(0, 10);
  };

  return (
    <div className={dynastyTheme.components.card.base}>
      <div className="p-6">
        <h3 className={dynastyTheme.components.heading.h3}>
          <BarChart3 className="inline w-5 h-5 mr-2" />
          Price Distribution Summary
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div>
            <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>Avg Price</div>
            <div className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>
              ${generatedPrices.summary.avg_price}
            </div>
          </div>
          <div>
            <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>Max Price</div>
            <div className={`text-2xl font-bold ${dynastyTheme.classes.text.success}`}>
              ${generatedPrices.summary.max_price}
            </div>
          </div>
          <div>
            <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>{'Players > $30'}</div>
            <div className={`text-2xl font-bold ${dynastyTheme.classes.text.warning}`}>
              {generatedPrices.summary.distribution?.over_30 || 0}
            </div>
          </div>
          <div>
            <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>Total Players</div>
            <div className={`text-2xl font-bold ${dynastyTheme.classes.text.primary}`}>
              {generatedPrices.prices?.length || 0}
            </div>
          </div>
        </div>
        
        {/* Distribution Chart */}
        <div className="mt-6">
          <h4 className={`text-md font-semibold mb-3 ${dynastyTheme.classes.text.warning}`}>
            Price Distribution
          </h4>
          <div className="space-y-2">
            {getDistributionChart()?.map(range => (
              <div key={range.label} className="flex items-center gap-3">
                <div className={`w-16 text-right text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                  {range.label}
                </div>
                <div className="flex-1 h-6 bg-gray-700 rounded relative overflow-hidden">
                  <div
                    className={`h-full ${range.color} transition-all duration-500`}
                    style={{ width: `${range.percentage}%` }}
                  />
                </div>
                <div className={`w-20 text-right text-sm ${dynastyTheme.classes.text.white}`}>
                  {range.count} <span className={dynastyTheme.classes.text.neutralLight}>({range.percentOfTotal}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Top 10 Players */}
        <div className="mt-6">
          <h4 className={`text-md font-semibold mb-3 ${dynastyTheme.classes.text.warning}`}>
            Top 10 Most Expensive Players
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {getTop10Players().map((player, idx) => {
              // Access canonical structure
              const playerId = player?.ids?.mlb;
              const playerName = player?.info?.full_name || 'Unknown';
              const position = player?.info?.position || '?';

              return (
                <div
                  key={playerId || idx}
                  className={`p-3 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border ${dynastyTheme.classes.border.neutral}`}
                >
                  <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                    #{idx + 1} • {position}
                  </div>
                  <div className={`text-sm font-semibold ${dynastyTheme.classes.text.white} truncate mb-1`}>
                    <PlayerLink mlbId={player?.ids?.mlb} name={playerName} style={{ fontWeight: 600, fontSize: '0.875rem', color: '#f9fafb' }} />
                  </div>
                  <div className={`text-lg font-bold ${dynastyTheme.classes.text.success}`}>
                    ${(player.pricing?.baseline_price || 0).toFixed(0)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceDistributionSummary;